// File: client/src/App.js
import React, { useState, useEffect } from "react";
import {
  ThemeProvider,
  CssBaseline,
  Container,
  Box,
  Typography,
  GlobalStyles, // <-- for removing default margins
} from "@mui/material";
import {
  Link,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  fetchPosts,
  getOrCreateUserId,
  verifyAdminKey,
  fetchInfiniteFeed,
  fetchPostById,
  getFollowedProfiles,
} from "./api";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";

import CreatePost from "./components/CreatePost";
import EmailSignup from "./components/EmailSignup";
import PostList from "./components/PostList";
import AIList from "./components/AIList";
import AIProfilePage from "./components/AIProfilePage";
import SuggestProfileForm from "./components/SuggestProfileForm";
import darkTheme from "./theme";

function NavBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const adminKey = localStorage.getItem("adminKey");

  /* true ⇒ bar shown, false ⇒ bar hidden */
  const [visible, setVisible] = React.useState(true);

  /* --- SCROLL HANDLER ---------------------------------------------------- */
  React.useEffect(() => {
    let prevY = window.pageYOffset || 0;
    let ticking = false;

    const handleScroll = () => {
      const currY = window.pageYOffset || 0;

      if (!ticking) {
        window.requestAnimationFrame(() => {
          const goingDown = currY > prevY;
          const pastHeader = currY > 64;
          setVisible(!(goingDown && pastHeader));

          prevY = currY;
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  /* ---------------------------------------------------------------------- */

  /* Helper to keep the admin key in all internal links */
  const withAdminKey = (path) =>
    adminKey ? `${path}?adminKey=${adminKey}` : path;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1300,
        borderBottom: 1,
        borderColor: "divider",
        backgroundColor: "background.default",
        backdropFilter: "blur(12px)",
        transform: visible ? "translateY(0)" : "translateY(-100%)",
        transition: "transform 0.25s ease",
        willChange: "transform",
      }}
    >
      <Container maxWidth="sm" disableGutters>
        <Box
          sx={{
            height: 56,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: { xs: 8, sm: 12 },
          }}
        >
          <Link
            to={withAdminKey("/")}
            onClick={(e) => {
              if (location.pathname === "/") {
                e.preventDefault();
                window.dispatchEvent(new Event("refreshFeed"));
              }
            }}
            style={{
              textDecoration: "none",
              color: "inherit",
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 10px",
              borderRadius: 8,
            }}
          >
            <HomeOutlinedIcon sx={{ fontSize: { xs: 20, sm: 22 } }} />
            <Typography sx={{ fontWeight: 700, fontSize: { xs: 16, sm: 17 } }}>
              Home
            </Typography>
          </Link>

          <Link
            to={withAdminKey("/ai")}
            style={{
              textDecoration: "none",
              color: "inherit",
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 10px",
              borderRadius: 8,
            }}
          >
            <PeopleOutlinedIcon sx={{ fontSize: { xs: 20, sm: 22 } }} />
            <Typography sx={{ fontWeight: 700, fontSize: { xs: 16, sm: 17 } }}>
              Profiles
            </Typography>
          </Link>
        </Box>
      </Container>
    </Box>
  );
}

function App() {
  const [initialPosts, setInitialPosts] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [feedVersion, setFeedVersion] = useState(0);
  const [sharedPostId, setSharedPostId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  // Add effect to reload posts when navigating to home
  useEffect(() => {
    if (location.pathname === "/") {
      loadInitialPosts();
    }
  }, [location.pathname]);

  /* --- handle manual Home refresh ------------------------------------ */
  useEffect(() => {
    const handleRefresh = () => {
      // (a) scroll to top
      window.scrollTo({ top: 0, behavior: "smooth" });
      // (b) re‑fetch the feed
      setInitialPosts([]); // wipe current list first
      setFeedVersion((v) => v + 1); // force PostList re‑mount
      loadInitialPosts(); // pull fresh data
    };

    window.addEventListener("refreshFeed", handleRefresh);
    return () => window.removeEventListener("refreshFeed", handleRefresh);
  }, []);
  /* ------------------------------------------------------------------- */

  // Check for admin mode in URL and localStorage
  useEffect(() => {
    const verifyAdmin = async () => {
      // Check URL parameters first
      const params = new URLSearchParams(location.search);
      const adminKeyFromUrl = params.get("adminKey");

      // Check localStorage
      const adminKeyFromStorage = localStorage.getItem("adminKey");

      // Use admin key from URL if available, otherwise from localStorage
      const adminKey = adminKeyFromUrl || adminKeyFromStorage;

      if (!adminKey) {
        setIsAdmin(false);
        return;
      }

      // If we have an admin key in localStorage but not in URL, add it to URL
      if (adminKeyFromStorage && !adminKeyFromUrl) {
        const newUrl = `${location.pathname}?adminKey=${adminKeyFromStorage}${location.hash}`;
        navigate(newUrl, { replace: true });
      }

      try {
        const isValid = await verifyAdminKey(adminKey);
        setIsAdmin(isValid);
      } catch (err) {
        console.error("Error verifying admin key:", err);
        setIsAdmin(false);
      }
    };

    verifyAdmin();
  }, [location.search, location.pathname, navigate]);

  const loadInitialPosts = async () => {
    try {
      setInitialLoading(true);
      // Use the infinite feed endpoint for initial load as well
      // This ensures consistency with the infinite scrolling behavior
      const data = await fetchInfiniteFeed([], 25, getFollowedProfiles());
      const userId = getOrCreateUserId();

      // transform each post => { likeCount, userHasLiked }
      const transformed = data.map((p) => ({
        ...p,
        likeCount:
          p.likeCount !== undefined ? p.likeCount : p.Likes?.length || 0,
        userHasLiked:
          p.userHasLiked !== undefined
            ? p.userHasLiked
            : p.Likes?.some((l) => l.userId === userId) || false,
      }));

      setInitialPosts(transformed);
    } catch (err) {
      console.error("Error loading initial posts:", err);
    } finally {
      setInitialLoading(false);
    }
  };

  // Reorder if ?share=123
  useEffect(() => {
    if (!initialPosts.length) return;

    // Get share parameter from either hash or query
    const hashParams = new URLSearchParams(
      window.location.hash.split("?")[1] || ""
    );
    const queryParams = new URLSearchParams(window.location.search);
    const sharedId = hashParams.get("share") || queryParams.get("share");

    if (sharedId) {
      const parsedId = parseInt(sharedId);
      setSharedPostId(parsedId);

      // Check if the shared post is already in initialPosts
      const postExists = initialPosts.some((post) => post.id === parsedId);

      // If the shared post is not in initialPosts, fetch it
      if (!postExists) {
        fetchSharedPost(parsedId);
      }
    } else {
      setSharedPostId(null);
    }
  }, [location.search, location.hash, initialPosts]);

  const fetchSharedPost = async (postId) => {
    try {
      const post = await fetchPostById(postId);

      // The server now provides likeCount and userHasLiked directly
      // Add the shared post to the beginning of initialPosts
      setInitialPosts((prev) => [post, ...prev]);
    } catch (err) {
      console.error("Error fetching shared post:", err);
    }
  };

  const handlePostCreated = (newPost) => {
    const newTransformed = {
      ...newPost,
      likeCount: 0,
      userHasLiked: false,
    };
    setInitialPosts((prev) => [newTransformed, ...prev]);
  };

  const handlePostUpdated = (updatedPost) => {
    setInitialPosts((prev) => {
      const idx = prev.findIndex((p) => p.id === updatedPost.id);
      if (idx === -1) return prev; // guard – should not happen
      const next = [...prev];
      next[idx] = updatedPost;
      return next;
    });
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      {/* GlobalStyles to remove default body/html margin */}
      <GlobalStyles
        styles={{
          "html, body, #root": {
            margin: 0,
            padding: 0,
            boxSizing: "border-box",
            width: "100%",
            minHeight: "100%",
            "@supports (height: 100dvh)": {
              minHeight: "100dvh",
            },
          },
        }}
      />
      <Box
        component="main"
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          width: "100%",
          margin: 0,
          padding: 0,
          overflowX: "hidden",
          paddingTop:
            location.pathname === "/" ? { xs: "56px", sm: "40px" } : "60px", // 40px on desktop for home, 56px for other pages and mobile
        }}
      >
        <NavBar />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            width: "100%",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: { xs: "100%", sm: "600px" },
              flex: 1,
            }}
          >
            <Routes>
              <Route
                path="/"
                element={
                  <Box
                    sx={{
                      p: { xs: 0, sm: 2 },
                    }}
                  >
                    {/* 1 – mailing list sign‑up always visible */}
                    <EmailSignup />

                    {/* 2 – post composer (only admins) */}
                    {isAdmin && (
                      <CreatePost onPostCreated={handlePostCreated} />
                    )}

                    {/* 3 – the actual feed */}
                    <PostList
                      key={`${location.pathname}-${feedVersion}`}
                      initialPosts={initialPosts}
                      initialLoading={initialLoading}
                      onPostUpdated={handlePostUpdated}
                      sharedPostId={sharedPostId}
                      isAdmin={isAdmin}
                      useInfiniteScroll={true}
                    />
                  </Box>
                }
              />
              <Route path="/ai" element={<AIList isAdmin={isAdmin} />} />
              <Route
                path="/ai/:id"
                element={<AIProfilePage isAdmin={isAdmin} />}
              />
              <Route path="/ai/suggest" element={<SuggestProfileForm />} />
              <Route
                path="*"
                element={
                  <Typography
                    sx={{ p: 4, textAlign: "center", color: "text.primary" }}
                  >
                    Page not found
                  </Typography>
                }
              />
            </Routes>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default App;
